import React, { useEffect, useReducer, MouseEvent, FormEvent } from 'react'
import { Button, Dropdown } from 'semantic-ui-react'

import useModal from 'hooks/useModal'
import ModalClickBoundary from 'shared/components/ModalClickBoundary'
import type { ACTIONTYPE } from './reducer'
import { reducer } from './reducer'
import SurveyAccessModalContent from './SurveyAccessModalContent'
import { AllowPermission } from 'shared/components/AllowPermission'
import constants from 'config/constants'

type InitialState = { users: UserAccess[] }
type Reducer = (prevState: InitialState, action: ACTIONTYPE) => InitialState
type IsAccessEditableRole = (user: UserAccess) => boolean

export type UserAccess = {
  user_id: number
  name: string
  role: string
  has_permission: boolean
  is_access_editable: boolean
}

type Props = {
  defaultUsers: UserAccess[]
  currentUserId: number
  defaultOpen?: boolean
  onChange: (data: number[]) => void
}

export default function SurveyAccessFormModal({
  defaultUsers,
  currentUserId,
  defaultOpen,
  onChange,
}: Props) {
  const { Modal, setOpen, open, close } = useModal()

  const _isAccessEditableUser: IsAccessEditableRole = (user) => user.is_access_editable

  const accessEditableUser = defaultUsers.filter(_isAccessEditableUser)

  const initialState: InitialState = {
    users: accessEditableUser,
  }
  const [state, dispatch] = useReducer<Reducer>(reducer, initialState)

  function onCheckboxChange(user: UserAccess) {
    if (user.has_permission) {
      dispatch({ type: 'remove_permission', payload: user.user_id })
    } else {
      dispatch({ type: 'add_permission', payload: user.user_id })
    }
  }

  function clearAllOptions(event: MouseEvent) {
    event.preventDefault()
    dispatch({ type: 'assign_permission_to_all_user', payload: false })
  }

  function selectAllOptions(event: MouseEvent | FormEvent) {
    event.preventDefault()
    dispatch({ type: 'assign_permission_to_all_user', payload: true })
  }

  function _getUserIds(users: UserAccess[]): number[] {
    return users.reduce((previousUserId: number[], user: UserAccess) => {
      return [...previousUserId, user.user_id]
    }, [])
  }

  function onSubmit(event: MouseEvent) {
    event.stopPropagation()

    const allowUserId = _getUserIds(
      state.users.filter((user) => user.has_permission && _isAccessEditableUser(user))
    )

    onChange(allowUserId)
    close()
  }

  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen, setOpen])

  return (
    <React.Fragment>
      <Dropdown.Item text="Add or remove user access" icon="far fa-user" onClick={open} />

      <ModalClickBoundary>
        <Modal>
          <Modal.Header>Survey access</Modal.Header>
          <Modal.Content>
            <div>
              <p>
                Choose who can view feedback and stats for this survey.{' '}
                <a
                  href="https://help.simplesat.io/en/articles/2361167-users-permissions#survey-access-and-why-a-dashboard-is-empty"
                  target="_blank"
                  rel="noopener"
                >
                  Learn more.
                </a>
              </p>
              <SurveyAccessModalContent
                clearAllOptions={clearAllOptions}
                currentUserId={currentUserId}
                onCheckboxChange={onCheckboxChange}
                selectAllOptions={selectAllOptions}
                users={state.users}
              />
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={(event) => {
                event.stopPropagation()
                close()
              }}
            >
              Cancel
            </Button>
            <AllowPermission permission={constants.permissions.manageUserAccessToSurveys}>
              <Button primary onClick={onSubmit}>
                Save
              </Button>
            </AllowPermission>
          </Modal.Actions>
        </Modal>
      </ModalClickBoundary>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/SurveyAccessFormModal/index.tsx